import { useCallback, useMemo, useState } from 'react';
import { toaster } from 'baseui/toast';
import { useAtomValue, useSetAtom } from 'jotai';
import { NO_SAM_REGION_DETECTED_FOR_CLASS_PROMPT, NO_SAM_REGION_DETECTED_FOR_CUSTOM_PROMPT, SAM_AUTO_DETECTION_FAILED } from '../../../../common/constant/constant';
import { useAPIProvider } from '../../../../common/hooks/use-api-provider';
import type { AnnotationLabelT, AnnotationUnderUserReviewT } from '../../../../common/types/types';
import { getRandomId } from '../../../../common/utils/utils';
import { EVENTS } from '../../../labelling-v2/atoms/label-store-dispatch/events';
import { debouncedPolygonEpsilonAtom, isLoadingAtom } from '../../../labelling-v2/atoms/tool-atoms';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import type { SAMSegmentImageData } from '../../../types';
import { SegmentImageModelName } from '../../../types';
import useAnalyticsForSAM from '../../analytics/useSAMAnalytics';
import { PROMPT_MODE } from '../../components/toolbar/auto-detect-segmentation-panel';
import { getSAM3LabelAnalyticsPayload } from '../../components/toolbar/utils';
import { DEFAULT_POLYGON_EPSILON } from '../../utils/mask-to-polygon';
type PromptModeT = (typeof PROMPT_MODE)[keyof typeof PROMPT_MODE];
export type Sam3AutoDetectBuildPendingItemArgsT = {
  result: SAMSegmentImageData;
  annotationId: string;
  selectedAnnotationLabel: AnnotationLabelT;
  textPrompt: string;
};
export type Sam3CommittedPendingItemT<TPending> = {
  data: TPending;
  annotationLabel: AnnotationLabelT;
};
export type Sam3AutoDetectConfigT<TPending> = {
  annotationType: 'POLYGON' | 'MASK';
  buildPendingItem: (args: Sam3AutoDetectBuildPendingItemArgsT) => Promise<{
    pending: TPending;
    reviewEntry: AnnotationUnderUserReviewT;
  } | undefined>;
  onSubmitAll: (pending: Sam3CommittedPendingItemT<TPending>[]) => void;
  onCancelAll?: (pending: Sam3CommittedPendingItemT<TPending>[]) => void;
  onDetectStart?: () => void;
};
type PendingEntry<TPending> = {
  annotationId: string;
  data: TPending;
};
export const useSam3AutoDetect = <TPending,>(config: Sam3AutoDetectConfigT<TPending>) => {
  const {
    selectedAnnotationLabelAtom,
    dataUrlAtom,
    dispatchAtom,
    currentFrameAnnotationUnderUserReviewAtom
  } = useStageAtoms();
  const selectedAnnotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const dataUrl = useAtomValue(dataUrlAtom);
  const dispatch = useSetAtom(dispatchAtom);
  const setIsLoading = useSetAtom(isLoadingAtom);
  const setDebouncedPolygonEpsilon = useSetAtom(debouncedPolygonEpsilonAtom);
  const annotationUnderUserReview = useAtomValue(currentFrameAnnotationUnderUserReviewAtom);
  const {
    getSAM3ImageDataPoints
  } = useAPIProvider();
  const {
    sendEventSam3AutoDetectTriggered,
    sendEventSam3AutoDetectResult,
    sendEventSam3AutoDetectSubmitAll,
    sendEventSam3AutoDetectCancelAll
  } = useAnalyticsForSAM();
  const [isDetecting, setIsDetecting] = useState(false);
  const [pending, setPending] = useState<PendingEntry<TPending>[]>([]);
  const [detectionAnnotationLabel, setDetectionAnnotationLabel] = useState<AnnotationLabelT | null>(null);
  const detect = useCallback(async (textPrompt: string, textPromptMode: PromptModeT): Promise<number> => {
    if (!getSAM3ImageDataPoints || !dataUrl || !selectedAnnotationLabel) return 0;
    const sharedLabelPayload = {
      ...getSAM3LabelAnalyticsPayload(selectedAnnotationLabel),
      annotationType: config.annotationType
    };
    sendEventSam3AutoDetectTriggered({
      ...sharedLabelPayload,
      textPromptMode,
      textPrompt
    });
    setIsDetecting(true);
    setIsLoading(true);
    setDetectionAnnotationLabel(selectedAnnotationLabel);
    setDebouncedPolygonEpsilon(DEFAULT_POLYGON_EPSILON);
    let hasResults = false;
    let detectedCount = 0;
    try {
      const results = await getSAM3ImageDataPoints({
        imageUrl: dataUrl,
        textPrompt,
        modelName: SegmentImageModelName.SEGMENT_IMAGE_MODEL_NAME_SAM3
      });
      sendEventSam3AutoDetectResult({
        ...sharedLabelPayload,
        detectedCount: results?.length ?? 0,
        success: true
      });
      if (!results || results.length === 0) {
        const noResultsMessage = textPromptMode === PROMPT_MODE.CUSTOM ? NO_SAM_REGION_DETECTED_FOR_CUSTOM_PROMPT : NO_SAM_REGION_DETECTED_FOR_CLASS_PROMPT;
        toaster.negative(noResultsMessage, {
          autoHideDuration: 5000
        });
        setDetectionAnnotationLabel(null);
        return 0;
      }
      const newPending: PendingEntry<TPending>[] = [];
      const batchedAnnotations: Record<string, AnnotationUnderUserReviewT> = {};
      for (const result of results) {
        if (!result.mask) continue;
        const annotationId = getRandomId();
        const built = await config.buildPendingItem({
          result,
          annotationId,
          selectedAnnotationLabel,
          textPrompt
        });
        if (!built) continue;
        batchedAnnotations[annotationId] = built.reviewEntry;
        newPending.push({
          annotationId,
          data: built.pending
        });
      }
      dispatch({
        type: EVENTS.ADD_ANNOTATION_UNDER_REVIEW,
        annotations: batchedAnnotations
      });
      setPending(newPending);
      detectedCount = newPending.length;
      hasResults = detectedCount > 0;
      // keep isLoading true during results review — cleared by submitAll/cancelAll
    } catch (error) {
      console.error('SAM3 auto-detect error:', error);
      sendEventSam3AutoDetectResult({
        ...sharedLabelPayload,
        detectedCount: 0,
        success: false
      });
      toaster.negative(SAM_AUTO_DETECTION_FAILED, {
        autoHideDuration: 5000
      });
      setDetectionAnnotationLabel(null);
    } finally {
      setIsDetecting(false);
      if (!hasResults) setIsLoading(false);
    }
    return detectedCount;
  }, [getSAM3ImageDataPoints, dataUrl, selectedAnnotationLabel, dispatch, setIsLoading, setDebouncedPolygonEpsilon, sendEventSam3AutoDetectTriggered, sendEventSam3AutoDetectResult, config]);
  const submitAll = useCallback(() => {
    if (!selectedAnnotationLabel || pending.length === 0) return;
    const sharedLabelPayload = {
      ...getSAM3LabelAnalyticsPayload(selectedAnnotationLabel),
      annotationType: config.annotationType
    };
    sendEventSam3AutoDetectSubmitAll({
      ...sharedLabelPayload,
      submittedCount: pending.length
    });
    config.onSubmitAll(pending.map(({
      annotationId,
      data
    }) => ({
      data,
      annotationLabel: annotationUnderUserReview[annotationId]?.annotationMetaData?.[0]?.annotationLabel ?? selectedAnnotationLabel
    })));
    dispatch({
      type: EVENTS.DELETE_ANNOTATION_UNDER_REVIEW,
      annotationIds: pending.map(({
        annotationId
      }) => annotationId)
    });
    setPending([]);
    setIsLoading(false);
    setDetectionAnnotationLabel(null);
    setDebouncedPolygonEpsilon(DEFAULT_POLYGON_EPSILON);
  }, [pending, selectedAnnotationLabel, annotationUnderUserReview, dispatch, setIsLoading, setDebouncedPolygonEpsilon, sendEventSam3AutoDetectSubmitAll, config]);
  const cancelAll = useCallback(() => {
    config.onCancelAll?.(pending.map(({
      annotationId,
      data
    }) => ({
      data,
      annotationLabel: annotationUnderUserReview[annotationId]?.annotationMetaData?.[0]?.annotationLabel ?? selectedAnnotationLabel
    })).filter((entry): entry is Sam3CommittedPendingItemT<TPending> => entry.annotationLabel !== undefined));
    sendEventSam3AutoDetectCancelAll({
      ...(selectedAnnotationLabel ? getSAM3LabelAnalyticsPayload(selectedAnnotationLabel) : {
        annotationLabelTitle: '',
        parentLabelTitle: '',
        isInstanceLabel: false
      }),
      annotationType: config.annotationType,
      pendingCount: pending.length
    });
    dispatch({
      type: EVENTS.DELETE_ANNOTATION_UNDER_REVIEW,
      annotationIds: pending.map(({
        annotationId
      }) => annotationId)
    });
    setPending([]);
    setIsLoading(false);
    setDetectionAnnotationLabel(null);
    setDebouncedPolygonEpsilon(DEFAULT_POLYGON_EPSILON);
  }, [pending, selectedAnnotationLabel, annotationUnderUserReview, dispatch, setIsLoading, setDebouncedPolygonEpsilon, sendEventSam3AutoDetectCancelAll, config]);
  const updateAllPending = useCallback((updater: (data: TPending) => {
    data: TPending;
    reviewEntry: Partial<AnnotationUnderUserReviewT>;
  }) => {
    setPending(currentPending => {
      if (currentPending.length === 0) return currentPending;
      const batchedAnnotations: Record<string, AnnotationUnderUserReviewT> = {};
      const updatedPending = currentPending.map(entry => {
        const {
          data,
          reviewEntry
        } = updater(entry.data);
        const existingReviewEntry = annotationUnderUserReview[entry.annotationId];
        if (existingReviewEntry) {
          // TPending is generic across callers, so TS can't verify reviewEntry's
          // annotationData shape matches existingReviewEntry's discriminated branch.
          batchedAnnotations[entry.annotationId] = ({
            ...existingReviewEntry,
            ...reviewEntry
          } as AnnotationUnderUserReviewT);
        }
        return {
          ...entry,
          data
        };
      });
      dispatch({
        type: EVENTS.ADD_ANNOTATION_UNDER_REVIEW,
        annotations: batchedAnnotations
      });
      return updatedPending;
    });
  }, [dispatch, annotationUnderUserReview]);
  const [firstPending] = pending;
  const pendingAnnotationLabel = useMemo(() => (firstPending && annotationUnderUserReview[firstPending.annotationId]?.annotationMetaData?.[0]?.annotationLabel) ?? detectionAnnotationLabel, [firstPending, annotationUnderUserReview, detectionAnnotationLabel]);
  return {
    detect,
    submitAll,
    cancelAll,
    updateAllPending,
    isDetecting,
    detectedCount: pending.length,
    pendingAnnotationLabel
  };
};